import { type FormEvent, useEffect, useMemo, useState } from "react"
import { Link } from "react-router-dom"
import { Box, Map, MapPin, Package, Plus, RefreshCw, Search } from "lucide-react"
import { smartoltService, type SmartOLTOdbs, type SmartOLTZones } from "../../../services/smartolt.service.ts"
import { toast } from "../../../utils/notify.ts"
import { SMARTOLT_PATHS } from "../smartoltNav.ts"
import { filterRows, formatUpdated } from "./smartoltSettingsUtils.ts"

const empty: SmartOLTOdbs = { items: [], summary: { total: 0, with_gps: 0, total_onus_linked: 0 }, updated_at: null }

const inputCls = "mt-1 h-9 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-900 font-normal placeholder-slate-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500/20"
const labelCls = "text-xs font-medium text-slate-700"

export function SmartOLTOdbsPage() {
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [data, setData] = useState<SmartOLTOdbs>(empty)
  const [zones, setZones] = useState<SmartOLTZones["zones"]>([])
  const [search, setSearch] = useState("")
  const [showAdd, setShowAdd] = useState(false)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState({ zone: "", name: "", nr_of_ports: "", latitude: "", longitude: "", external_id: "" })

  const load = async (silent = false, sync = false) => {
    if (!silent) setLoading(true)
    setRefreshing(true)
    try {
      if (sync) {
        try { await smartoltService.runSyncNow(25) } catch { /* list cached data */ }
      }
      const [next, zoneData] = await Promise.all([
        smartoltService.getOdbs(),
        typeof smartoltService.getZones === "function" ? smartoltService.getZones().catch(() => null) : Promise.resolve(null),
      ])
      setData({ items: next?.items || [], summary: next?.summary || empty.summary, updated_at: next?.updated_at || null })
      setZones(Array.isArray(zoneData?.zones) ? zoneData.zones : [])
    } catch {
      toast.error("Failed to load ODBs")
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  const filtered = useMemo(() => filterRows(data.items, search, ["name", "odb_id", "zone_name"]), [data.items, search])

  const updateForm = (field: keyof typeof form, value: string) => {
    setForm((current) => ({ ...current, [field]: value }))
  }

  const addOdb = async (event: FormEvent) => {
    event.preventDefault()
    if (!form.zone.trim() || !form.name.trim()) { toast.error("Zone and ODB name are required"); return }
    const form_data = Object.fromEntries(
      Object.entries(form).map(([key, value]) => [key, value.trim()]).filter(([, value]) => value),
    )
    setSaving(true)
    try {
      await smartoltService.createCommand("add_odb", { form_data })
      await smartoltService.runCommandsNow(5)
      toast.success("ODB add command sent")
      setForm({ zone: "", name: "", nr_of_ports: "", latitude: "", longitude: "", external_id: "" })
      setShowAdd(false)
      await load(true)
    } catch (error: any) {
      toast.error(error?.response?.data?.error || "Failed to add ODB")
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-900">ODB/ODF/Closures</h1>
          <p className="mt-0.5 text-xs text-slate-500">Optical distribution boxes, closure boxes & fiber splitters</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="rounded-lg bg-slate-100 border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700">
            {data.summary.total} items
          </span>
          <span className="rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-1.5 text-xs font-semibold text-emerald-700">
            {data.summary.with_gps} GPS
          </span>
          <button type="button" onClick={() => setShowAdd((v) => !v)}
            className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 text-sm font-medium text-slate-700 shadow-xs transition-colors hover:bg-slate-50 hover:text-blue-600">
            <Plus className="h-3.5 w-3.5" />
            Add ODB
          </button>
          <Link to={SMARTOLT_PATHS.maps}
            className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 px-3 text-sm font-medium text-white shadow-xs transition-colors">
            <Map className="h-3.5 w-3.5" />
            Open Map
          </Link>
        </div>
      </div>

      {/* Add ODB form */}
      {showAdd && (
        <form onSubmit={addOdb} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-xs">
          <p className="mb-4 text-sm font-semibold text-slate-800">Add New ODB/ODF</p>
          <div className="grid gap-3 sm:grid-cols-3">
            <label className={labelCls}>
              Zone *
              <select value={form.zone} onChange={(e) => updateForm("zone", e.target.value)}
                className={inputCls}>
                <option value="">Select zone</option>
                {zones.map((zone) => (
                  <option key={zone.zone_id} value={zone.zone_id}>{zone.name}</option>
                ))}
              </select>
            </label>
            <label className={labelCls}>
              Name *
              <input value={form.name} onChange={(e) => updateForm("name", e.target.value)} className={inputCls} />
            </label>
            <label className={labelCls}>
              Ports
              <input value={form.nr_of_ports} onChange={(e) => updateForm("nr_of_ports", e.target.value)} className={inputCls} type="number" />
            </label>
            <label className={labelCls}>
              Latitude
              <input value={form.latitude} onChange={(e) => updateForm("latitude", e.target.value)} className={inputCls} />
            </label>
            <label className={labelCls}>
              Longitude
              <input value={form.longitude} onChange={(e) => updateForm("longitude", e.target.value)} className={inputCls} />
            </label>
            <label className={labelCls}>
              External ID
              <input value={form.external_id} onChange={(e) => updateForm("external_id", e.target.value)} className={inputCls} />
            </label>
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <button type="button" onClick={() => setShowAdd(false)}
              className="h-9 rounded-lg border border-slate-200 bg-white px-4 text-sm font-medium text-slate-700 shadow-xs hover:bg-slate-50 transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={saving}
              className="h-9 rounded-lg bg-blue-600 hover:bg-blue-700 px-5 text-sm font-medium text-white shadow-xs disabled:opacity-50 transition-colors">
              {saving ? "Adding…" : "Add ODB"}
            </button>
          </div>
        </form>
      )}

      {/* Table */}
      <div className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-xs">
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 bg-slate-50/50 p-3">
          <div className="relative min-w-[180px] flex-1 sm:max-w-xs">
            <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search ODBs…"
              className="h-9 w-full rounded-lg border border-slate-200 bg-white pl-8 pr-3 text-sm text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500/20" />
          </div>
          <button type="button" disabled={refreshing} onClick={() => load(true, true)}
            className="ml-auto inline-flex h-9 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 text-sm font-medium text-slate-700 shadow-xs transition-colors hover:bg-slate-50 hover:text-blue-600 disabled:opacity-50">
            <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
            Sync from SmartOLT
          </button>
        </div>

        {loading ? (
          <div className="px-4 py-12 text-center">
            <RefreshCw className="mx-auto h-7 w-7 animate-spin text-blue-500 mb-2" />
            <p className="text-slate-500 text-sm">Loading ODBs…</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="px-4 py-14 text-center">
            <Package className="mx-auto h-12 w-12 text-slate-300 mb-3" />
            <p className="text-sm font-semibold text-slate-800">No ODBs, ODFs or closures yet</p>
            <p className="mt-1 text-xs text-slate-500">Items sync from SmartOLT and appear on the network map when GPS is set.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="border-b border-slate-200 bg-slate-50">
                <tr>
                  {["Name", "Zone", "ONUs", "GPS", "Synced"].map((h, i) => (
                    <th key={h} className={`px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-slate-600 ${i === 2 ? "text-right" : "text-left"}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((row) => (
                  <tr key={row.odb_id} className="transition-colors hover:bg-slate-50">
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-2">
                        <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-blue-50">
                          <Box className="h-3.5 w-3.5 text-blue-600" />
                        </div>
                        <span className="font-medium text-slate-800">{row.name}</span>
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-slate-600">{row.zone_name || row.zone_id || "—"}</td>
                    <td className="px-3 py-2.5 text-right font-semibold text-slate-700">{row.onus}</td>
                    <td className="px-3 py-2.5">
                      {row.has_gps ? (
                        <span className="inline-flex items-center gap-1 text-emerald-700 text-xs font-semibold">
                          <MapPin className="h-3 w-3" /> GPS
                        </span>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 font-mono text-xs text-slate-500">{formatUpdated(row.fetched_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
